import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { Menu, X } from 'lucide-react';
import Navigation from './Navigation';
import { siteConfig } from '../config/site';
import { useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AuthModal from './AuthModal';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: ${({ isScrolled }) =>
    isScrolled ? 'rgba(255, 255, 255, 0.95)' : 'transparent'};
  backdrop-filter: ${({ isScrolled }) => (isScrolled ? 'blur(16px)' : 'none')};
  border-bottom: ${({ isScrolled }) =>
    isScrolled ? '1px solid rgba(0, 0, 0, 0.1)' : 'none'};
  box-shadow: ${({ isScrolled }) =>
    isScrolled ? '0 4px 20px rgba(0, 0, 0, 0.1)' : 'none'};
  transition: all 0.3s ease;
  z-index: 50;
`;

const HeaderWrapper = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 64px;

  @media (min-width: 1024px) {
    padding: 0 2rem;
    height: 72px;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LogoIcon = styled.div`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
`;

const Logo = styled.a`
  font-size: 1.8rem;
  font-weight: 700;
  color: ${({ isScrolled }) => (isScrolled ? '#1f2937' : 'white')};
  letter-spacing: -0.025em;
  text-decoration: none;
  transition: color 0.3s ease;

  &:hover {
    color: ${({ isScrolled }) =>
      isScrolled ? '#7c3aed' : 'rgba(255, 255, 255, 0.8)'};
  }

  @media (min-width: 768px) {
    font-size: 2.5rem;
  }
`;

const MobileMenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 8px;
  background: ${({ isScrolled }) =>
    isScrolled ? 'rgba(107, 114, 128, 0.1)' : 'rgba(255, 255, 255, 0.2)'};
  backdrop-filter: blur(8px);
  color: ${({ isScrolled }) => (isScrolled ? '#374151' : 'white')};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ isScrolled }) =>
      isScrolled ? 'rgba(107, 114, 128, 0.2)' : 'rgba(255, 255, 255, 0.3)'};
    transform: scale(1.05);
  }

  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileMenuOverlay = styled.div`
  display: ${(props) => (props.isOpen ? 'block' : 'none')};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.95);
  backdrop-filter: blur(16px);
  z-index: 60;
  animation: ${(props) => (props.isOpen ? 'fadeIn' : 'fadeOut')} 0.3s ease;

  @media (min-width: 768px) {
    display: none;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes fadeOut {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }
`;

const MobileMenuContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  padding: 0 2rem;
  text-align: center;
`;

const MobileMenuHeader = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
  padding: 0 1.5rem;
`;

const MobileCloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
  }
`;

const MobileNavigation = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin-bottom: 3rem;
`;

const MobileNavLink = styled.a`
  font-size: 2.5rem;
  font-weight: 300;
  color: white;
  text-decoration: none;
  transition: all 0.3s ease;
  opacity: 0;
  transform: translateY(20px);
  animation: slideInUp 0.6s ease forwards;
  animation-delay: ${(props) => props.delay || '0ms'};

  &:hover {
    color: #a855f7;
    transform: translateY(-2px) scale(1.05);
  }

  @keyframes slideInUp {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const MobileCTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 2rem;
  background: linear-gradient(45deg, #7c3aed, #ec4899);
  color: white;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.125rem;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px rgba(124, 58, 237, 0.3);
  opacity: 0;
  transform: translateY(20px);
  animation: slideInUp 0.6s ease forwards;
  animation-delay: 500ms;

  &:hover {
    transform: translateY(-2px) scale(1.05);
    box-shadow: 0 15px 40px rgba(124, 58, 237, 0.4);
  }
`;

const DecorativeElements = styled.div`
  position: absolute;
  top: 20%;
  left: 2rem;
  width: 120px;
  height: 120px;
  background: rgba(168, 85, 247, 0.2);
  border-radius: 50%;
  filter: blur(40px);
  animation: float 6s ease-in-out infinite;

  &::after {
    content: '';
    position: absolute;
    bottom: 20%;
    right: 2rem;
    width: 80px;
    height: 80px;
    background: rgba(236, 72, 153, 0.2);
    border-radius: 50%;
    filter: blur(30px);
    animation: float 6s ease-in-out infinite reverse;
  }

  @keyframes float {
    0%,
    100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-20px);
    }
  }
`;

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const isHomePage = location.pathname === '/' || location.pathname === '/home';

  const logoWhite = new URL('../assets/logo-white.png', import.meta.url).href;
  const logoBlack = new URL('../assets/logo-black.png', import.meta.url).href;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = (e, id) => {
    e.preventDefault();

    // If we're not on the home page and trying to access a section via #,
    // navigate to home page first
    if (!isHomePage && id.startsWith('#')) {
      window.location.href = '/' + id;
      return;
    }

    const sectionId = id.replace('#', '');
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <HeaderContainer isScrolled={isScrolled}>
        <HeaderWrapper>
          <LogoContainer>
            <LogoIcon isScrolled={isScrolled}>
              <img src={isScrolled ? logoBlack : logoWhite} alt="Qraft" />
            </LogoIcon>
            <Logo href="/" isScrolled={isScrolled}>
              {siteConfig.siteName}
            </Logo>
          </LogoContainer>

          <MobileMenuButton
            isScrolled={isScrolled}
            onClick={() => setIsMobileMenuOpen(true)}
            aria-label="Open mobile menu"
          >
            <Menu size={20} />
          </MobileMenuButton>

          <Navigation isScrolled={isScrolled} isHomePage={isHomePage} />
          {user ? (
            <Link
              to="/account"
              style={{
                padding: '0.5rem 1.25rem',
                borderRadius: '0.5rem',
                background: '#7c3aed',
                color: '#ffffff',
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: '0.95rem',
              }}
            >
              Account
            </Link>
          ) : (
            <button
              onClick={() => setAuthModalOpen(true)}
              style={{
                padding: '0.5rem 1.25rem',
                borderRadius: '0.5rem',
                background: '#7c3aed',
                color: '#ffffff',
                border: 'none',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.95rem',
                fontFamily: 'inherit',
              }}
            >
              Sign In
            </button>
          )}
        </HeaderWrapper>
      </HeaderContainer>

      <MobileMenuOverlay isOpen={isMobileMenuOpen}>
        <MobileMenuHeader>
          <LogoContainer>
            <LogoIcon isScrolled={false}>
              <img src={logoWhite} alt="Qraft" />
            </LogoIcon>
            <Logo href="/" isScrolled={false}>
              {siteConfig.siteName}
            </Logo>
          </LogoContainer>

          <MobileCloseButton
            onClick={() => setIsMobileMenuOpen(false)}
            aria-label="Close mobile menu"
          >
            <X size={20} />
          </MobileCloseButton>
        </MobileMenuHeader>

        <MobileMenuContent>
          <DecorativeElements />

          <MobileNavigation>
            {siteConfig.navItems.map((item, index) =>
              item.label === 'Try It Out' ? (
                <MobileCTAButton
                  key={item.label}
                  as={Link}
                  to={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.label}
                </MobileCTAButton>
              ) : (
                <MobileNavLink
                  key={item.label}
                  as={item.href.startsWith('#') ? 'a' : Link}
                  href={item.href.startsWith('#') ? item.href : undefined}
                  to={!item.href.startsWith('#') ? item.href : undefined}
                  delay={`${index * 100}ms`}
                  onClick={(e) => {
                    if (item.href.startsWith('#')) {
                      scrollToSection(e, item.href);
                    } else {
                      setIsMobileMenuOpen(false);
                    }
                  }}
                >
                  {item.label}
                </MobileNavLink>
              ),
            )}
            {user ? (
              <MobileNavLink
                as={Link}
                to="/account"
                delay="400ms"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Account
              </MobileNavLink>
            ) : (
              <MobileNavLink
                as="button"
                delay="400ms"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  setAuthModalOpen(true);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                }}
              >
                Sign In
              </MobileNavLink>
            )}
          </MobileNavigation>
        </MobileMenuContent>
      </MobileMenuOverlay>
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
      />
    </>
  );
}
